// Driver Mode Toggle Component
// Allows users to enable/disable driver tracking mode

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigation,
  MapPin,
  Shield,
  AlertTriangle,
  Check,
  Loader2,
  Signal,
  Battery,
} from 'lucide-react';
import { useDriverGeolocation } from '../hooks/useDriverGeolocation';
import clsx from 'clsx';

interface DriverModeToggleProps {
  variant?: 'full' | 'compact' | 'button';
  className?: string;
}

export function DriverModeToggle({
  variant = 'full',
  className = '',
}: DriverModeToggleProps) {
  const { t } = useTranslation();
  const {
    isTracking,
    isSupported,
    currentPosition,
    error,
    accuracy,
    startTracking,
    stopTracking,
  } = useDriverGeolocation();

  const [isLoading, setIsLoading] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  // Handle toggle
  const handleToggle = async () => {
    if (isTracking) {
      stopTracking();
      return;
    }

    // Show consent dialog first
    if (!showConsent) {
      setShowConsent(true);
      return;
    }
  };

  // Handle consent accepted
  const handleConsentAccept = async () => {
    setShowConsent(false);
    setIsLoading(true);

    const success = await startTracking();

    setIsLoading(false);

    if (!success) {
      // Error will be in the hook state
    }
  };

  // Handle consent declined
  const handleConsentDecline = () => {
    setShowConsent(false);
  };

  // Button variant
  if (variant === 'button') {
    return (
      <button
        onClick={handleToggle}
        disabled={!isSupported || isLoading}
        className={clsx(
          'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
          isTracking
            ? 'bg-green-500 text-white hover:bg-green-600'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          (!isSupported || isLoading) && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Navigation
            className={clsx('w-4 h-4', isTracking && 'animate-pulse')}
          />
        )}
        {isTracking ? t('driver_mode.button_active') : t('driver_mode.button_label')}
      </button>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <div className={clsx('flex items-center gap-3', className)}>
        <button
          onClick={handleToggle}
          disabled={!isSupported || isLoading}
          className={clsx(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
            isTracking ? 'bg-green-500' : 'bg-gray-300',
            (!isSupported || isLoading) && 'opacity-50 cursor-not-allowed'
          )}
        >
          <span
            className={clsx(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              isTracking ? 'translate-x-6' : 'translate-x-1'
            )}
          >
            {isLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            )}
          </span>
        </button>
        <span className="text-sm font-medium text-gray-700">
          {t('driver_mode.button_label')}
        </span>
        {isTracking && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <Signal className="w-3 h-3" />
            {t('driver_mode.active_short')}
          </span>
        )}
      </div>
    );
  }

  // Full variant (default)
  return (
    <div className={clsx('space-y-4', className)}>
      {/* Consent Dialog */}
      {showConsent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('driver_mode.consent_title')}
                </h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                {t('driver_mode.consent_intro')}
              </p>

              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5" />
                  {t('driver_mode.consent_item_1')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5" />
                  {t('driver_mode.consent_item_2')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5" />
                  {t('driver_mode.consent_item_3')}
                </li>
              </ul>

              <div className="flex gap-3">
                <button
                  onClick={handleConsentDecline}
                  className="flex-1 px-4 py-2.5 text-gray-700 font-medium border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  {t('driver_mode.consent_cancel')}
                </button>
                <button
                  onClick={handleConsentAccept}
                  className="flex-1 px-4 py-2.5 bg-cyan-600 text-white font-medium rounded-xl hover:bg-cyan-700 transition-colors"
                >
                  {t('driver_mode.consent_accept')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="p-4 bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl">
        <div className="flex items-start gap-4">
          <div
            className={clsx(
              'p-3 rounded-xl',
              isTracking ? 'bg-green-500' : 'bg-cyan-100'
            )}
          >
            <Navigation
              className={clsx(
                'w-6 h-6',
                isTracking ? 'text-white animate-pulse' : 'text-cyan-600'
              )}
            />
          </div>

          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">{t('driver_mode.card_title')}</h3>
              <button
                onClick={handleToggle}
                disabled={!isSupported || isLoading}
                className={clsx(
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  isTracking ? 'bg-green-500' : 'bg-gray-300',
                  (!isSupported || isLoading) && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                    isTracking ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-3">
              {isTracking
                ? t('driver_mode.card_subtitle_active')
                : t('driver_mode.card_subtitle_inactive')}
            </p>

            {/* Status when tracking */}
            {isTracking && currentPosition && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <MapPin className="w-3.5 h-3.5 text-green-500" />
                  <span>
                    {currentPosition.coords.latitude.toFixed(5)},{' '}
                    {currentPosition.coords.longitude.toFixed(5)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Signal className="w-3.5 h-3.5 text-green-500" />
                  <span>{t('driver_mode.accuracy_label', { value: accuracy?.toFixed(0) ?? '?' })}</span>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mt-2 flex items-center gap-2 text-xs text-red-600">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Not supported message */}
            {!isSupported && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-600">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>{t('driver_mode.unsupported')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Battery warning when tracking */}
      {isTracking && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
          <Battery className="w-4 h-4" />
          <span>
            {t('driver_mode.battery_warning')}
          </span>
        </div>
      )}
    </div>
  );
}

export default DriverModeToggle;
